:root {
  /* Typography */
  --vx-btn-font-family: 'Inter', sans-serif;
  --vx-btn-font-weight: 500;

  /* Shape / layout */
  --vx-btn-border-radius: 4px;
  --vx-btn-border-width: 1px;
  --vx-btn-margin: 4px;
  --vx-btn-gap: 0.5rem;
  --vx-btn-gap-xs: 0.375rem;

  /* Motion */
  --vx-btn-transition: all 0.2s ease-in-out;
  --vx-btn-active-translate-y: 0.5px;
  --vx-btn-hover-opacity: 0.9;
  --vx-btn-active-opacity: 0.85;
  --vx-btn-disabled-opacity: 0.5;
  --vx-btn-loading-opacity: 0.7;

  /* Shadows */
  --vx-btn-primary-hover-shadow: 0 1px 2px color-mix(in srgb, black 10%, transparent);

  /* Sizing */
  --vx-btn-height-xs: 28px;
  --vx-btn-height-sm: 32px;
  --vx-btn-height-md: 36px;
  --vx-btn-height-lg: 36px;

  --vx-btn-width-xs-icon: 28px;
  --vx-btn-width-sm-icon: 32px;
  --vx-btn-width-md-icon: 36px;
  --vx-btn-width-lg-icon: 36px;

  --vx-btn-padding-x-xs: 9px;
  --vx-btn-padding-x-sm: 13px;
  --vx-btn-padding-x-md: 17px;
  --vx-btn-padding-x-lg: 17px;

  --vx-btn-padding-x-left-icon-xs: 7px;
  --vx-btn-padding-x-left-icon-sm: 10px;
  --vx-btn-padding-x-left-icon-md: 12px;
  --vx-btn-padding-x-left-icon-lg: 12px;

  --vx-btn-padding-x-right-icon-xs: 7px;
  --vx-btn-padding-x-right-icon-sm: 10px;
  --vx-btn-padding-x-right-icon-md: 12px;
  --vx-btn-padding-x-right-icon-lg: 12px;

  --vx-btn-font-size-xs: 11px;
  --vx-btn-font-size-sm: 12px;
  --vx-btn-font-size-md: 13px;
  --vx-btn-font-size-lg: 13px;

  /* Core palette */
  --vx-btn-color-white: #ffffff;
  --vx-btn-color-off-white: #f9f9f9;
  --vx-btn-color-black: #000000;
  --vx-btn-color-ink: #101011;
  --vx-btn-color-blue: var(--PRIMARY-BLUE-1);
  --vx-btn-color-red: #ef4444;

  --vx-btn-color-zinc-100: #f5f5f6;
  --vx-btn-color-zinc-200: #e9eaec;
  --vx-btn-color-zinc-300: #d4d5d9;
  --vx-btn-color-zinc-350: #d4d4d8;
  --vx-btn-color-zinc-400: #a1a1aa;
  --vx-btn-color-zinc-500: #71717a;
  --vx-btn-color-zinc-600: #52525b;
  --vx-btn-color-zinc-700: #3f3f46;
  --vx-btn-color-zinc-750: #4a4a52;
  --vx-btn-color-zinc-800: #27272a;
  --vx-btn-color-zinc-400-dark: #a1a1aa;

  /* Transparent helpers */
  --vx-btn-transparent: transparent;
  --vx-btn-border-transparent: color-mix(in srgb, black 0%, transparent);

  /* Primary */
  --vx-btn-primary-bg: var(--vx-btn-color-blue);
  --vx-btn-primary-fg: var(--vx-btn-color-off-white);
  --vx-btn-primary-border: var(--vx-btn-color-blue);

  /* Secondary - light */
  --vx-btn-secondary-bg: var(--vx-btn-color-zinc-200);
  --vx-btn-secondary-fg: var(--vx-btn-color-ink);
  --vx-btn-secondary-border: var(--vx-btn-border-transparent);
  --vx-btn-secondary-hover-bg: var(--vx-btn-color-zinc-300);
  --vx-btn-secondary-active-bg: var(--vx-btn-color-zinc-350);

  /* Secondary - dark */
  --vx-btn-secondary-dark-bg: var(--vx-btn-color-zinc-800);
  --vx-btn-secondary-dark-fg: var(--vx-btn-color-off-white);
  --vx-btn-secondary-dark-hover-bg: var(--vx-btn-color-zinc-700);
  --vx-btn-secondary-dark-active-bg: var(--vx-btn-color-zinc-750);

  /* Outline - light */
  --vx-btn-outline-bg: var(--vx-btn-transparent);
  --vx-btn-outline-fg: var(--vx-btn-color-ink);
  --vx-btn-outline-border: var(--vx-btn-color-zinc-350);
  --vx-btn-outline-hover-bg: color-mix(in srgb, var(--vx-btn-color-black) 4%, transparent);
  --vx-btn-outline-active-bg: color-mix(in srgb, var(--vx-btn-color-black) 8%, transparent);

  /* Outline - dark */
  --vx-btn-outline-dark-fg: var(--vx-btn-color-off-white);
  --vx-btn-outline-dark-border: color-mix(in srgb, var(--vx-dropdown-color-white) 10%, transparent);
  --vx-btn-outline-dark-hover-bg: color-mix(in srgb, var(--vx-btn-color-white) 5%, transparent);
  --vx-btn-outline-dark-active-bg: color-mix(in srgb, var(--vx-btn-color-white) 10%, transparent);

  /* Ghost - light */
  --vx-btn-ghost-bg: var(--vx-btn-transparent);
  --vx-btn-ghost-fg: var(--vx-btn-color-zinc-500);
  --vx-btn-ghost-border: var(--vx-btn-transparent);
  --vx-btn-ghost-hover-bg: color-mix(in srgb, var(--vx-btn-color-black) 5%, transparent);
  --vx-btn-ghost-hover-fg: var(--vx-btn-color-ink);
  --vx-btn-ghost-active-bg: color-mix(in srgb, var(--vx-btn-color-black) 10%, transparent);

  /* Ghost - dark */
  --vx-btn-ghost-dark-fg: var(--vx-btn-color-zinc-400-dark);
  --vx-btn-ghost-dark-hover-bg: color-mix(in srgb, var(--vx-btn-color-white) 5%, transparent);
  --vx-btn-ghost-dark-hover-fg: var(--vx-btn-color-off-white);
  --vx-btn-ghost-dark-active-bg: color-mix(in srgb, var(--vx-btn-color-white) 10%, transparent);

  /* Destructive */
  --vx-btn-destructive-bg: var(--vx-btn-color-red);
  --vx-btn-destructive-fg: var(--vx-btn-color-white);
  --vx-btn-destructive-border: var(--vx-btn-color-red);

  /* Destructive ghost - base */
  --vx-btn-destructive-ghost-bg: color-mix(in srgb, var(--vx-btn-color-red) 8%, transparent);
  --vx-btn-destructive-ghost-fg: color-mix(in srgb, var(--vx-btn-color-red) 65%, transparent);
  --vx-btn-destructive-ghost-border: color-mix(in srgb, var(--vx-btn-color-red) 15%, transparent);
  --vx-btn-destructive-ghost-hover-bg: color-mix(in srgb, var(--vx-btn-color-red) 15%, transparent);
  --vx-btn-destructive-ghost-hover-fg: var(--vx-btn-color-red);
  --vx-btn-destructive-ghost-hover-border: color-mix(
    in srgb,
    var(--vx-btn-color-red) 30%,
    transparent
  );
  --vx-btn-destructive-ghost-active-bg: color-mix(
    in srgb,
    var(--vx-btn-color-red) 20%,
    transparent
  );
  --vx-btn-destructive-ghost-active-fg: var(--vx-btn-color-red);

  /* Destructive ghost - dark */
  --vx-btn-destructive-ghost-dark-bg: color-mix(in srgb, var(--vx-btn-color-red) 8%, transparent);
  --vx-btn-destructive-ghost-dark-fg: color-mix(in srgb, var(--vx-btn-color-red) 65%, transparent);
  --vx-btn-destructive-ghost-dark-border: color-mix(
    in srgb,
    var(--vx-btn-color-red) 12%,
    transparent
  );
  --vx-btn-destructive-ghost-dark-hover-bg: color-mix(
    in srgb,
    var(--vx-btn-color-red) 18%,
    transparent
  );
  --vx-btn-destructive-ghost-dark-hover-fg: var(--vx-btn-color-red);
  --vx-btn-destructive-ghost-dark-hover-border: color-mix(
    in srgb,
    var(--vx-btn-color-red) 30%,
    transparent
  );
  --vx-btn-destructive-ghost-dark-active-bg: color-mix(
    in srgb,
    var(--vx-btn-color-red) 25%,
    transparent
  );
  --vx-btn-destructive-ghost-dark-active-fg: var(--vx-btn-color-red);

  /* Destructive ghost - light override */
  --vx-btn-destructive-ghost-light-bg: color-mix(in srgb, var(--vx-btn-color-red) 6%, transparent);
  --vx-btn-destructive-ghost-light-fg: color-mix(in srgb, var(--vx-btn-color-red) 65%, transparent);
  --vx-btn-destructive-ghost-light-border: color-mix(
    in srgb,
    var(--vx-btn-color-red) 15%,
    transparent
  );
  --vx-btn-destructive-ghost-light-hover-bg: color-mix(
    in srgb,
    var(--vx-btn-color-red) 12%,
    transparent
  );
  --vx-btn-destructive-ghost-light-hover-fg: var(--vx-btn-color-red);
  --vx-btn-destructive-ghost-light-hover-border: color-mix(
    in srgb,
    var(--vx-btn-color-red) 30%,
    transparent
  );
  --vx-btn-destructive-ghost-light-active-bg: color-mix(
    in srgb,
    var(--vx-btn-color-red) 18%,
    transparent
  );
  --vx-btn-destructive-ghost-light-active-fg: var(--vx-btn-color-red);

  /* Spinner */
  --vx-btn-spinner-size: 1em;
  --vx-btn-spinner-duration: 1s;

  /* Selected / toggled state */
  --vx-btn-primary-selected-bg: color-mix(in srgb, var(--vx-btn-color-blue) 92%, black);
  --vx-btn-primary-selected-fg: var(--vx-btn-color-off-white);
  --vx-btn-primary-selected-border: color-mix(in srgb, var(--vx-btn-color-blue) 88%, black);

  --vx-btn-secondary-selected-bg: var(--vx-btn-secondary-active-bg);
  --vx-btn-secondary-selected-fg: var(--vx-btn-secondary-fg);
  --vx-btn-secondary-selected-border: var(--vx-btn-secondary-border);

  --vx-btn-secondary-dark-selected-bg: var(--vx-btn-secondary-dark-active-bg);
  --vx-btn-secondary-dark-selected-fg: var(--vx-btn-secondary-dark-fg);
  --vx-btn-secondary-dark-selected-border: var(--vx-btn-secondary-border);

  --vx-btn-outline-selected-bg: color-mix(in srgb, var(--vx-btn-color-black) 8%, transparent);
  --vx-btn-outline-selected-fg: var(--vx-btn-outline-fg);
  --vx-btn-outline-selected-border: color-mix(
    in srgb,
    var(--vx-btn-outline-border) 75%,
    var(--vx-btn-color-ink)
  );

  --vx-btn-outline-dark-selected-bg: color-mix(in srgb, var(--vx-btn-color-white) 10%, transparent);
  --vx-btn-outline-dark-selected-fg: var(--vx-btn-outline-dark-fg);
  --vx-btn-outline-dark-selected-border: color-mix(
    in srgb,
    var(--vx-btn-outline-dark-border) 70%,
    white
  );

  --vx-btn-ghost-selected-bg: color-mix(in srgb, var(--vx-btn-color-black) 10%, transparent);
  --vx-btn-ghost-selected-fg: var(--vx-btn-color-ink);
  --vx-btn-ghost-selected-border: var(--vx-btn-ghost-border);

  --vx-btn-ghost-dark-selected-bg: color-mix(in srgb, var(--vx-btn-color-white) 10%, transparent);
  --vx-btn-ghost-dark-selected-fg: var(--vx-btn-color-off-white);
  --vx-btn-ghost-dark-selected-border: var(--vx-btn-ghost-border);

  --vx-btn-destructive-selected-bg: color-mix(in srgb, var(--vx-btn-color-red) 92%, black);
  --vx-btn-destructive-selected-fg: var(--vx-btn-color-white);
  --vx-btn-destructive-selected-border: color-mix(in srgb, var(--vx-btn-color-red) 88%, black);

  --vx-btn-destructive-ghost-selected-bg: var(--vx-btn-destructive-ghost-active-bg);
  --vx-btn-destructive-ghost-selected-fg: var(--vx-btn-destructive-ghost-active-fg);
  --vx-btn-destructive-ghost-selected-border: var(--vx-btn-destructive-ghost-hover-border);

  --vx-btn-destructive-ghost-dark-selected-bg: var(--vx-btn-destructive-ghost-dark-active-bg);
  --vx-btn-destructive-ghost-dark-selected-fg: var(--vx-btn-destructive-ghost-dark-active-fg);
  --vx-btn-destructive-ghost-dark-selected-border: var(
    --vx-btn-destructive-ghost-dark-hover-border
  );

  --vx-btn-destructive-ghost-light-selected-bg: var(--vx-btn-destructive-ghost-light-active-bg);
  --vx-btn-destructive-ghost-light-selected-fg: var(--vx-btn-destructive-ghost-light-active-fg);
  --vx-btn-destructive-ghost-light-selected-border: var(
    --vx-btn-destructive-ghost-light-hover-border
  );
}

.vx-btn {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--vx-btn-border-radius);
  font-family: var(--vx-btn-font-family), serif;
  font-weight: var(--vx-btn-font-weight);
  transition: var(--vx-btn-transition);
  cursor: pointer;
  white-space: nowrap;
  border: var(--vx-btn-border-width) solid transparent;
  outline: none;
  gap: var(--vx-btn-gap);
  margin: var(--vx-btn-margin);
}

.vx-btn:disabled {
  opacity: var(--vx-btn-disabled-opacity);
  pointer-events: none;
}

/* Sizes */

.vx-btn--xs {
  height: var(--vx-btn-height-xs);
  padding-left: var(--vx-btn-padding-x-xs);
  padding-right: var(--vx-btn-padding-x-xs);
  font-size: var(--vx-btn-font-size-xs);
  gap: var(--vx-btn-gap-xs);
}

.vx-btn--sm {
  height: var(--vx-btn-height-sm);
  padding-left: var(--vx-btn-padding-x-sm);
  padding-right: var(--vx-btn-padding-x-sm);
  font-size: var(--vx-btn-font-size-sm);
}

.vx-btn--md {
  height: var(--vx-btn-height-md);
  padding-left: var(--vx-btn-padding-x-md);
  padding-right: var(--vx-btn-padding-x-md);
  font-size: var(--vx-btn-font-size-md);
}

.vx-btn--lg {
  height: var(--vx-btn-height-lg);
  padding-left: var(--vx-btn-padding-x-lg);
  padding-right: var(--vx-btn-padding-x-lg);
  font-size: var(--vx-btn-font-size-lg);
}

/* Optical balance for icon + label */

.vx-btn--md:has(.vx-btn__icon-left) {
  padding-left: var(--vx-btn-padding-x-left-icon-md);
}

.vx-btn--lg:has(.vx-btn__icon-left) {
  padding-left: var(--vx-btn-padding-x-left-icon-lg);
}

.vx-btn--sm:has(.vx-btn__icon-left) {
  padding-left: var(--vx-btn-padding-x-left-icon-sm);
}

.vx-btn--xs:has(.vx-btn__icon-left) {
  padding-left: var(--vx-btn-padding-x-left-icon-xs);
}

.vx-btn--md:has(.vx-btn__icon-right) {
  padding-right: var(--vx-btn-padding-x-right-icon-md);
}

.vx-btn--lg:has(.vx-btn__icon-right) {
  padding-right: var(--vx-btn-padding-x-right-icon-lg);
}

.vx-btn--sm:has(.vx-btn__icon-right) {
  padding-right: var(--vx-btn-padding-x-right-icon-sm);
}

.vx-btn--xs:has(.vx-btn__icon-right) {
  padding-right: var(--vx-btn-padding-x-right-icon-xs);
}

/* Icon size mode */

.vx-btn--icon {
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.vx-btn--icon.vx-btn--xs {
  width: var(--vx-btn-width-xs-icon);
  height: var(--vx-btn-height-xs);
}

.vx-btn--icon.vx-btn--sm {
  width: var(--vx-btn-height-sm);
  height: var(--vx-btn-height-sm);
}

.vx-btn--icon.vx-btn--md,
.vx-btn--icon.vx-btn--lg {
  width: var(--vx-btn-width-md-icon);
  height: var(--vx-btn-height-md);
}

/* Icon-only padding fix */

.vx-btn--icon-only {
  gap: 0;
  padding-left: inherit;
  padding-right: inherit;
}

.vx-btn--icon-only.vx-btn--md,
.vx-btn--icon-only.vx-btn--lg {
  padding-left: var(--vx-btn-padding-x-md);
  padding-right: var(--vx-btn-padding-x-md);
  height: var(--vx-btn-height-md);
  width: var(--vx-btn-height-md);
}

.vx-btn--icon-only.vx-btn--sm {
  padding-left: var(--vx-btn-padding-x-sm);
  padding-right: var(--vx-btn-padding-x-sm);
  height: var(--vx-btn-height-sm);
  width: var(--vx-btn-height-sm);
}

.vx-btn--icon-only.vx-btn--xs {
  padding-left: var(--vx-btn-padding-x-xs);
  padding-right: var(--vx-btn-padding-x-xs);
  height: var(--vx-btn-height-xs);
  width: var(--vx-btn-height-xs);
}

/* Primary */

.vx-btn--primary {
  background-color: var(--vx-btn-primary-bg);
  color: var(--vx-btn-primary-fg);
  border-color: var(--vx-btn-primary-border);
}

.vx-btn--primary:hover {
  opacity: var(--vx-btn-hover-opacity);
  box-shadow: var(--vx-btn-primary-hover-shadow);
}

.vx-btn--primary:active {
  opacity: var(--vx-btn-active-opacity);
  transform: translateY(var(--vx-btn-active-translate-y));
}

/* Secondary */

.vx-btn--secondary {
  background-color: var(--vx-btn-secondary-bg);
  color: var(--vx-btn-secondary-fg);
  border-color: var(--vx-btn-secondary-border);
}

.vx-btn--secondary:hover {
  background-color: var(--vx-btn-secondary-hover-bg);
  color: var(--vx-btn-secondary-fg);
}

.vx-btn--secondary:active {
  background-color: var(--vx-btn-secondary-active-bg);
}

.vx-btn--secondary svg {
  stroke: var(--vx-btn-secondary-fg);
  color: var(--vx-btn-secondary-fg);
}

.vx-btn--secondary:hover svg {
  stroke: var(--vx-btn-secondary-fg);
  color: var(--vx-btn-secondary-fg);
}

.dark .vx-btn--secondary {
  background-color: var(--vx-btn-secondary-dark-bg);
  color: var(--vx-btn-secondary-dark-fg);
  border-color: var(--vx-btn-secondary-border);
}

.dark .vx-btn--secondary:hover {
  background-color: var(--vx-btn-secondary-dark-hover-bg);
  color: var(--vx-btn-secondary-dark-fg);
}

.dark .vx-btn--secondary:active {
  background-color: var(--vx-btn-secondary-dark-active-bg);
}

.dark .vx-btn--secondary svg,
.dark .vx-btn--secondary:hover svg {
  stroke: var(--vx-btn-secondary-dark-fg);
  color: var(--vx-btn-secondary-dark-fg);
}

.light .vx-btn--secondary {
  background-color: var(--vx-btn-secondary-bg);
  color: var(--vx-btn-secondary-fg);
  border-color: var(--vx-btn-secondary-border);
}

.light .vx-btn--secondary:hover {
  background-color: var(--vx-btn-secondary-hover-bg);
  color: var(--vx-btn-secondary-fg);
}

.light .vx-btn--secondary:active {
  background-color: var(--vx-btn-secondary-active-bg);
}

.light .vx-btn--secondary svg,
.light .vx-btn--secondary:hover svg {
  stroke: var(--vx-btn-secondary-fg);
  color: var(--vx-btn-secondary-fg);
}

/* Outline */

.vx-btn--outline {
  background-color: var(--vx-btn-outline-bg);
  border-color: var(--vx-btn-outline-border);
  color: var(--vx-btn-outline-fg);
}

.vx-btn--outline:hover {
  background-color: var(--vx-btn-outline-hover-bg);
  color: var(--vx-btn-outline-fg);
}

.vx-btn--outline:active {
  background-color: var(--vx-btn-outline-active-bg);
}

.vx-btn--outline svg {
  stroke: var(--vx-btn-outline-fg);
  color: var(--vx-btn-outline-fg);
}

.dark .vx-btn--outline {
  border-color: var(--vx-btn-outline-dark-border);
  color: var(--vx-btn-outline-dark-fg);
}

.dark .vx-btn--outline:hover {
  background-color: var(--vx-btn-outline-dark-hover-bg);
  color: var(--vx-btn-outline-dark-fg);
}

.dark .vx-btn--outline:active {
  background-color: var(--vx-btn-outline-dark-active-bg);
}

.dark .vx-btn--outline svg {
  stroke: var(--vx-btn-outline-dark-fg);
  color: var(--vx-btn-outline-dark-fg);
}

.light .vx-btn--outline {
  background-color: var(--vx-btn-outline-bg);
  border-color: var(--vx-btn-outline-border);
  color: var(--vx-btn-outline-fg);
}

.light .vx-btn--outline:hover {
  background-color: var(--vx-btn-outline-hover-bg);
  color: var(--vx-btn-outline-fg);
}

.light .vx-btn--outline:active {
  background-color: var(--vx-btn-outline-active-bg);
}

.light .vx-btn--outline svg {
  stroke: var(--vx-btn-outline-fg);
  color: var(--vx-btn-outline-fg);
}

/* Ghost */

.vx-btn--ghost {
  background-color: var(--vx-btn-ghost-bg);
  color: var(--vx-btn-ghost-fg);
  border-color: var(--vx-btn-ghost-border);
}

.vx-btn--ghost:hover {
  background-color: var(--vx-btn-ghost-hover-bg);
  color: var(--vx-btn-ghost-hover-fg);
}

.vx-btn--ghost:active {
  background-color: var(--vx-btn-ghost-active-bg);
}

.dark .vx-btn--ghost {
  color: var(--vx-btn-ghost-dark-fg);
}

.dark .vx-btn--ghost:hover {
  background-color: var(--vx-btn-ghost-dark-hover-bg);
  color: var(--vx-btn-ghost-dark-hover-fg);
}

.dark .vx-btn--ghost:active {
  background-color: var(--vx-btn-ghost-dark-active-bg);
}

.light .vx-btn--ghost {
  background-color: var(--vx-btn-ghost-bg);
  color: var(--vx-btn-ghost-fg);
  border-color: var(--vx-btn-ghost-border);
}

.light .vx-btn--ghost:hover {
  background-color: var(--vx-btn-ghost-hover-bg);
  color: var(--vx-btn-ghost-hover-fg);
}

.light .vx-btn--ghost:active {
  background-color: var(--vx-btn-ghost-active-bg);
}

/* Destructive */

.vx-btn--destructive {
  background-color: var(--vx-btn-destructive-bg);
  color: var(--vx-btn-destructive-fg);
  border-color: var(--vx-btn-destructive-border);
}

.vx-btn--destructive:hover {
  opacity: var(--vx-btn-hover-opacity);
}

.vx-btn--destructive:active {
  opacity: var(--vx-btn-active-opacity);
  transform: translateY(var(--vx-btn-active-translate-y));
}

/* Destructive ghost */

.vx-btn--destructive-ghost {
  background-color: var(--vx-btn-destructive-ghost-bg);
  color: var(--vx-btn-destructive-ghost-fg);
  border-color: var(--vx-btn-destructive-ghost-border);
}

.vx-btn--destructive-ghost:hover {
  background-color: var(--vx-btn-destructive-ghost-hover-bg);
  color: var(--vx-btn-destructive-ghost-hover-fg);
  border-color: var(--vx-btn-destructive-ghost-hover-border);
}

.vx-btn--destructive-ghost:active {
  background-color: var(--vx-btn-destructive-ghost-active-bg);
  color: var(--vx-btn-destructive-ghost-active-fg);
}

.dark .vx-btn--destructive-ghost {
  background-color: var(--vx-btn-destructive-ghost-dark-bg);
  color: var(--vx-btn-destructive-ghost-dark-fg);
  border-color: var(--vx-btn-destructive-ghost-dark-border);
}

.dark .vx-btn--destructive-ghost:hover {
  background-color: var(--vx-btn-destructive-ghost-dark-hover-bg);
  color: var(--vx-btn-destructive-ghost-dark-hover-fg);
  border-color: var(--vx-btn-destructive-ghost-dark-hover-border);
}

.dark .vx-btn--destructive-ghost:active {
  background-color: var(--vx-btn-destructive-ghost-dark-active-bg);
  color: var(--vx-btn-destructive-ghost-dark-active-fg);
}

.light .vx-btn--destructive-ghost {
  background-color: var(--vx-btn-destructive-ghost-light-bg);
  color: var(--vx-btn-destructive-ghost-light-fg);
  border-color: var(--vx-btn-destructive-ghost-light-border);
}

.light .vx-btn--destructive-ghost:hover {
  background-color: var(--vx-btn-destructive-ghost-light-hover-bg);
  color: var(--vx-btn-destructive-ghost-light-hover-fg);
  border-color: var(--vx-btn-destructive-ghost-light-hover-border);
}

.light .vx-btn--destructive-ghost:active {
  background-color: var(--vx-btn-destructive-ghost-light-active-bg);
  color: var(--vx-btn-destructive-ghost-light-active-fg);
}

/* Loading */

.vx-btn--loading {
  pointer-events: none;
  opacity: var(--vx-btn-loading-opacity);
}

.vx-btn__spinner {
  width: var(--vx-btn-spinner-size);
  height: var(--vx-btn-spinner-size);
  animation: spin var(--vx-btn-spinner-duration) linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Persistent selected state */

.vx-btn.active,
.vx-btn[aria-pressed='true'] {
  box-shadow: none;
}

/* Primary */
.vx-btn--primary.active,
.vx-btn--primary[aria-pressed='true'] {
  background-color: var(--vx-btn-primary-selected-bg);
  color: var(--vx-btn-primary-selected-fg);
  border-color: var(--vx-btn-primary-selected-border);
}

.vx-btn--primary.active:hover,
.vx-btn--primary[aria-pressed='true']:hover {
  background-color: var(--vx-btn-primary-selected-bg);
  color: var(--vx-btn-primary-selected-fg);
  border-color: var(--vx-btn-primary-selected-border);
  opacity: 1;
  box-shadow: none;
}

/* Secondary */
.vx-btn--secondary.active,
.vx-btn--secondary[aria-pressed='true'] {
  background-color: var(--vx-btn-secondary-selected-bg);
  color: var(--vx-btn-secondary-selected-fg);
  border-color: var(--vx-btn-secondary-selected-border);
}

.vx-btn--secondary.active:hover,
.vx-btn--secondary[aria-pressed='true']:hover {
  background-color: var(--vx-btn-secondary-selected-bg);
  color: var(--vx-btn-secondary-selected-fg);
}

.dark .vx-btn--secondary.active,
.dark .vx-btn--secondary[aria-pressed='true'] {
  background-color: var(--vx-btn-secondary-dark-selected-bg);
  color: var(--vx-btn-secondary-dark-selected-fg);
  border-color: var(--vx-btn-secondary-dark-selected-border);
}

.dark .vx-btn--secondary.active:hover,
.dark .vx-btn--secondary[aria-pressed='true']:hover {
  background-color: var(--vx-btn-secondary-dark-selected-bg);
  color: var(--vx-btn-secondary-dark-selected-fg);
}

.light .vx-btn--secondary.active,
.light .vx-btn--secondary[aria-pressed='true'] {
  background-color: var(--vx-btn-secondary-selected-bg);
  color: var(--vx-btn-secondary-selected-fg);
  border-color: var(--vx-btn-secondary-selected-border);
}

.light .vx-btn--secondary.active:hover,
.light .vx-btn--secondary[aria-pressed='true']:hover {
  background-color: var(--vx-btn-secondary-selected-bg);
  color: var(--vx-btn-secondary-selected-fg);
}

/* Outline */
.vx-btn--outline.active,
.vx-btn--outline[aria-pressed='true'] {
  background-color: var(--vx-btn-outline-selected-bg);
  color: var(--vx-btn-outline-selected-fg);
  border-color: var(--vx-btn-outline-selected-border);
}

.vx-btn--outline.active:hover,
.vx-btn--outline[aria-pressed='true']:hover {
  background-color: var(--vx-btn-outline-selected-bg);
  color: var(--vx-btn-outline-selected-fg);
  border-color: var(--vx-btn-outline-selected-border);
}

.dark .vx-btn--outline.active,
.dark .vx-btn--outline[aria-pressed='true'] {
  background-color: var(--vx-btn-outline-dark-selected-bg);
  color: var(--vx-btn-outline-dark-selected-fg);
  border-color: var(--vx-btn-outline-dark-selected-border);
}

.dark .vx-btn--outline.active:hover,
.dark .vx-btn--outline[aria-pressed='true']:hover {
  background-color: var(--vx-btn-outline-dark-selected-bg);
  color: var(--vx-btn-outline-dark-selected-fg);
  border-color: var(--vx-btn-outline-dark-selected-border);
}

.light .vx-btn--outline.active,
.light .vx-btn--outline[aria-pressed='true'] {
  background-color: var(--vx-btn-outline-selected-bg);
  color: var(--vx-btn-outline-selected-fg);
  border-color: var(--vx-btn-outline-selected-border);
}

.light .vx-btn--outline.active:hover,
.light .vx-btn--outline[aria-pressed='true']:hover {
  background-color: var(--vx-btn-outline-selected-bg);
  color: var(--vx-btn-outline-selected-fg);
  border-color: var(--vx-btn-outline-selected-border);
}

/* Ghost */
.vx-btn--ghost.active,
.vx-btn--ghost[aria-pressed='true'] {
  background-color: var(--vx-btn-ghost-selected-bg);
  color: var(--vx-btn-ghost-selected-fg);
  border-color: var(--vx-btn-ghost-selected-border);
}

.vx-btn--ghost.active:hover,
.vx-btn--ghost[aria-pressed='true']:hover {
  background-color: var(--vx-btn-ghost-selected-bg);
  color: var(--vx-btn-ghost-selected-fg);
}

.dark .vx-btn--ghost.active,
.dark .vx-btn--ghost[aria-pressed='true'] {
  background-color: var(--vx-btn-ghost-dark-selected-bg);
  color: var(--vx-btn-ghost-dark-selected-fg);
  border-color: var(--vx-btn-ghost-dark-selected-border);
}

.dark .vx-btn--ghost.active:hover,
.dark .vx-btn--ghost[aria-pressed='true']:hover {
  background-color: var(--vx-btn-ghost-dark-selected-bg);
  color: var(--vx-btn-ghost-dark-selected-fg);
}

.light .vx-btn--ghost.active,
.light .vx-btn--ghost[aria-pressed='true'] {
  background-color: var(--vx-btn-ghost-selected-bg);
  color: var(--vx-btn-ghost-selected-fg);
  border-color: var(--vx-btn-ghost-selected-border);
}

.light .vx-btn--ghost.active:hover,
.light .vx-btn--ghost[aria-pressed='true']:hover {
  background-color: var(--vx-btn-ghost-selected-bg);
  color: var(--vx-btn-ghost-selected-fg);
}

/* Destructive */
.vx-btn--destructive.active,
.vx-btn--destructive[aria-pressed='true'] {
  background-color: var(--vx-btn-destructive-selected-bg);
  color: var(--vx-btn-destructive-selected-fg);
  border-color: var(--vx-btn-destructive-selected-border);
}

.vx-btn--destructive.active:hover,
.vx-btn--destructive[aria-pressed='true']:hover {
  background-color: var(--vx-btn-destructive-selected-bg);
  color: var(--vx-btn-destructive-selected-fg);
  border-color: var(--vx-btn-destructive-selected-border);
  opacity: 1;
}

/* Destructive ghost */
.vx-btn--destructive-ghost.active,
.vx-btn--destructive-ghost[aria-pressed='true'] {
  background-color: var(--vx-btn-destructive-ghost-selected-bg);
  color: var(--vx-btn-destructive-ghost-selected-fg);
  border-color: var(--vx-btn-destructive-ghost-selected-border);
}

.vx-btn--destructive-ghost.active:hover,
.vx-btn--destructive-ghost[aria-pressed='true']:hover {
  background-color: var(--vx-btn-destructive-ghost-selected-bg);
  color: var(--vx-btn-destructive-ghost-selected-fg);
  border-color: var(--vx-btn-destructive-ghost-selected-border);
}

.dark .vx-btn--destructive-ghost.active,
.dark .vx-btn--destructive-ghost[aria-pressed='true'] {
  background-color: var(--vx-btn-destructive-ghost-dark-selected-bg);
  color: var(--vx-btn-destructive-ghost-dark-selected-fg);
  border-color: var(--vx-btn-destructive-ghost-dark-selected-border);
}

.dark .vx-btn--destructive-ghost.active:hover,
.dark .vx-btn--destructive-ghost[aria-pressed='true']:hover {
  background-color: var(--vx-btn-destructive-ghost-dark-selected-bg);
  color: var(--vx-btn-destructive-ghost-dark-selected-fg) !important;
  border-color: var(--vx-btn-destructive-ghost-dark-selected-border);
}

.light .vx-btn--destructive-ghost.active,
.light .vx-btn--destructive-ghost[aria-pressed='true'] {
  background-color: var(--vx-btn-destructive-ghost-light-selected-bg);
  color: var(--vx-btn-destructive-ghost-light-selected-fg);
  border-color: var(--vx-btn-destructive-ghost-light-selected-border);
}

.light .vx-btn--destructive-ghost.active:hover,
.light .vx-btn--destructive-ghost[aria-pressed='true']:hover {
  background-color: var(--vx-btn-destructive-ghost-light-selected-bg);
  color: var(--vx-btn-destructive-ghost-light-selected-fg);
  border-color: var(--vx-btn-destructive-ghost-light-selected-border);
}
.dark .vx-btn--destructive-ghost.vx-btn--icon-only span i {
  color: var(--vx-btn-destructive-ghost-dark-selected-fg) !important;
}

/* Help / info icons — borderless + faded (front-office pattern) */
.vx-btn.vx-help-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 !important;
  min-width: 0 !important;
  min-height: 0 !important;
  height: auto !important;
  width: auto !important;
  border: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  color: var(--TEXT-TERTIARY, var(--MUTED)) !important;
  opacity: 0.55;
}

.vx-btn.vx-help-icon:hover,
.vx-btn.vx-help-icon:focus-visible {
  background: transparent !important;
  color: var(--TEXT-SECONDARY, var(--MUTED)) !important;
  opacity: 1;
}

.vx-btn.vx-help-icon .icon-Information {
  line-height: 1;
  font-size: 0.875rem;
}

.vx-btn.vx-help-icon .icon-Information.xxs-small {
  font-size: 0.75rem;
}
